import React from 'react';
import PropTypes from 'prop-types';
import { Drawer, Position } from '@blueprintjs/core';

// The app's New and Edit forms: a panel that slides in from the left (as Edit
// details on a client's page) instead of a dialog in the middle of the screen.
//
//   <FormDrawer isOpen title="New estimate" icon="document" onClose={…} busy={saving}
//     footer={<><Button onClick={close}>Cancel</Button><Button intent="primary">Create estimate</Button></>}>
//     …fields…
//   </FormDrawer>
//
// The body scrolls; the footer stays at the bottom. While `busy`, Escape and
// clicking outside don't close it; Escape with a picker open closes only the
// picker. Each kind of record has its own accent (header strip, icon tile,
// focus colour), picked from the title or set with `tone`.
// Styles: styles/form-drawer.scss (.fd).

// Escape while a dropdown, date picker or address list is open should close
// only that, not the whole panel (and lose what was typed).
const pickerOpen = () =>
  typeof document !== 'undefined' &&
  Boolean(document.querySelector('.bp3-popover, .geosuggest__suggests:not(.geosuggest__suggests--hidden)'));

// Kind of record -> tone, from the title. First match wins.
const TONES = [
  ['billing', /\b(invoice|payment|charge|tax|transaction)\b/i],
  ['jobs', /\b(job|agreement)\b/i],
  ['estimates', /\b(estimate|scope of work|template)\b/i],
  ['field', /\b(work order|equipment|part|product|labor|material|form|service type|category)\b/i],
  ['team', /\b(note|time log|vacation|user)\b/i],
  ['customers', /\b(client|supplier|location|project)\b/i],
];

const toneFor = (title) => {
  const text = typeof title === 'string' ? title : '';
  const match = TONES.find(([, pattern]) => pattern.test(text));
  return match ? match[0] : 'customers';
};

const FormDrawer = ({ isOpen, title, icon, tone, onClose, onOpened, busy, footer, size, className, children }) => (
  <Drawer
    className={`fd fd--${tone || toneFor(title)}${className ? ` ${className}` : ''}`}
    title={title}
    icon={icon}
    position={Position.LEFT}
    size={size || 'min(560px, 100vw)'}
    isOpen={isOpen}
    onClose={busy ? undefined : (event) => {
      if (event && event.type === 'keydown' && pickerOpen()) return;
      onClose(event);
    }}
    onOpened={onOpened}
    canOutsideClickClose={!busy}
    canEscapeKeyClose={!busy}
  >
    <div className="fd__body">{children}</div>
    {footer && <div className="fd__footer">{footer}</div>}
  </Drawer>
);

FormDrawer.propTypes = {
  isOpen: PropTypes.bool,
  title: PropTypes.node,
  icon: PropTypes.string,
  tone: PropTypes.oneOf(['customers', 'jobs', 'estimates', 'billing', 'field', 'team']),
  onClose: PropTypes.func,
  onOpened: PropTypes.func,
  busy: PropTypes.bool,
  footer: PropTypes.node,
  size: PropTypes.string,
  className: PropTypes.string,
  children: PropTypes.node,
};

export default FormDrawer;
